import { useMemo, useState } from "react";
import { RefreshCw, Download, Search } from "lucide-react";

const STATUS_OPTIONS = [
  ["waiting", "Waiting"],
  ["contacted", "Contacted"],
  ["converted", "Converted"],
  ["removed", "Removed"],
];
const STATUS_BADGE = {
  waiting: "bg-amber-500/20 text-amber-300",
  contacted: "bg-sky-500/20 text-sky-300",
  converted: "bg-emerald-500/20 text-emerald-300",
  removed: "bg-white/10 text-white/40",
};

function toCsv(rows) {
  const cols = ["created_at", "program_title", "age_group", "day", "time", "start_date", "end_date", "athlete_name", "parent_name", "email", "phone", "status"];
  const head = ["Submitted", "Program", "Division", "Day", "Time", "Start", "End", "Athlete", "Parent/Guardian", "Email", "Phone", "Status"];
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lines = [head.join(",")];
  rows.forEach((r) => lines.push(cols.map((c) => esc(r[c])).join(",")));
  return lines.join("\n");
}

export default function ProgramWaitlistTab({ ctx }) {
  const { programWaitlist, programs, loading, refreshProgramWaitlist, setWaitlistStatus } = ctx;
  const [q, setQ] = useState("");
  const [programId, setProgramId] = useState("");

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (programWaitlist || []).filter((e) => {
      if (programId && e.program_id !== programId) return false;
      if (s && !`${e.athlete_name} ${e.parent_name} ${e.email} ${e.phone} ${e.program_title}`.toLowerCase().includes(s)) return false;
      return true;
    });
  }, [programWaitlist, q, programId]);

  const exportCsv = () => {
    const blob = new Blob([toCsv(filtered)], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `rep-lab-waitlist-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const selectCls = "bg-white/5 border border-white/10 rounded-xl px-3 py-2.5 text-white text-sm outline-none focus:border-[#2563EB] transition-colors";

  return (
    <>
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h1 className="text-3xl md:text-4xl font-extrabold tracking-tight text-white">Waitlist</h1>
        <div className="flex items-center gap-2">
          <button data-testid="waitlist-export-btn" onClick={exportCsv} disabled={filtered.length === 0}
            className="inline-flex items-center gap-2 text-sm font-bold text-white border border-white/15 hover:bg-white/10 disabled:opacity-40 rounded-full px-4 py-2.5 transition-colors">
            <Download size={15} /> Export CSV
          </button>
          <button data-testid="waitlist-refresh-btn" onClick={refreshProgramWaitlist} disabled={loading}
            className="inline-flex items-center gap-2 text-sm font-bold text-white/70 hover:text-white border border-white/15 rounded-full px-4 py-2.5 transition-colors">
            <RefreshCw size={15} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>
      <p className="mt-3 text-sm font-medium text-white/50">Waitlist submissions are kept separate from paid enrollments and never count toward program capacity.</p>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px]">
          <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-white/30" />
          <input data-testid="waitlist-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search athlete, parent, email…" className={`${selectCls} w-full pl-9`} />
        </div>
        <select data-testid="waitlist-filter-program" value={programId} onChange={(e) => setProgramId(e.target.value)} className={selectCls}>
          <option value="" className="bg-[#0B0F19]">All Programs</option>
          {(programs || []).map((p) => <option key={p.id} value={p.id} className="bg-[#0B0F19]">{p.title}</option>)}
        </select>
      </div>

      <p className="mt-5 text-xs font-bold uppercase tracking-wider text-white/40">{filtered.length} submission{filtered.length === 1 ? "" : "s"}</p>

      <div className="mt-3 space-y-3" data-testid="waitlist-list">
        {filtered.length === 0 && <p className="text-white/40 font-medium">No waitlist submissions match your filters.</p>}
        {filtered.map((e) => (
          <div key={e.id} data-testid={`waitlist-${e.id}`} className="rounded-2xl bg-white/5 border border-white/10 p-5 flex flex-wrap items-center justify-between gap-4">
            <div className="min-w-[240px]">
              <div className="flex items-center gap-2 flex-wrap">
                <span className={`inline-block rounded-full px-3 py-1 text-xs font-bold uppercase ${STATUS_BADGE[e.status] || STATUS_BADGE.waiting}`}>{e.status || "waiting"}</span>
                <span className="text-xs font-bold uppercase tracking-wider text-[#60a5fa]">{e.program_title}</span>
              </div>
              <p className="mt-2 text-lg font-bold text-white">{e.athlete_name || "—"}</p>
              <p className="mt-0.5 text-sm font-medium text-white/60">{[e.age_group, e.day, e.time, [e.start_date, e.end_date].filter(Boolean).join("–")].filter(Boolean).join(" · ")}</p>
              <p className="mt-0.5 text-sm font-medium text-white/40">{e.parent_name} · {e.email} · {e.phone || "no phone"}</p>
              {e.created_at && <p className="mt-0.5 text-xs font-medium text-white/30">Submitted {new Date(e.created_at).toLocaleString()}</p>}
            </div>
            <select data-testid={`waitlist-status-${e.id}`} value={e.status || "waiting"} onChange={(ev) => setWaitlistStatus(e.id, ev.target.value)}
              className="text-xs font-bold text-white bg-white/5 border border-white/15 rounded-full px-3 py-2 outline-none focus:border-[#2563EB] transition-colors">
              {STATUS_OPTIONS.map(([v, l]) => <option key={v} value={v} className="bg-[#0B0F19]">{l}</option>)}
            </select>
          </div>
        ))}
      </div>
    </>
  );
}
